import React from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuBan, LuCalendar, LuCheck, LuChevronDown, LuClock, LuLoader, LuUsersRound } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { EASE_OUT } from '../../../components/ui/motionPresets';
import { useTranslation } from '../../../i18n/LanguageContext';
import { isOverdue } from '../useMyTasks';

// Colours are classes, not inline styles: on legacy dark pages the shield in
// tailwind.css outranks inline colours on tw-* elements.
const PRIORITY_STYLES = {
  high: { bar: 'tw-bg-rose-500', text: 'tw-text-rose-600 dark:tw-text-rose-300' },
  medium: { bar: 'tw-bg-amber-500', text: 'tw-text-amber-600 dark:tw-text-amber-300' },
  low: { bar: 'tw-bg-emerald-500', text: 'tw-text-emerald-600 dark:tw-text-emerald-300' },
};

const STATUS_STYLES = {
  pending: {
    icon: LuClock,
    tone: 'neutral',
    on: 'tw-bg-slate-600 tw-text-white tw-ring-slate-600 dark:tw-bg-slate-500',
    off: 'tw-text-slate-600 tw-ring-slate-400/50 hover:tw-bg-slate-500/10 dark:tw-text-slate-300',
  },
  'in-progress': {
    icon: LuLoader,
    tone: 'info',
    on: 'tw-bg-blue-600 tw-text-white tw-ring-blue-600',
    off: 'tw-text-blue-700 tw-ring-blue-400/50 hover:tw-bg-blue-500/10 dark:tw-text-blue-300',
  },
  completed: {
    icon: LuCheck,
    tone: 'success',
    on: 'tw-bg-emerald-600 tw-text-white tw-ring-emerald-600',
    off: 'tw-text-emerald-700 tw-ring-emerald-400/50 hover:tw-bg-emerald-500/10 dark:tw-text-emerald-300',
  },
  cancelled: {
    icon: LuBan,
    tone: 'danger',
    on: 'tw-bg-rose-600 tw-text-white tw-ring-rose-600',
    off: 'tw-text-rose-700 tw-ring-rose-400/50 hover:tw-bg-rose-500/10 dark:tw-text-rose-300',
  },
};

/** One task in "Görevlerim": summary row, expanding to details and status actions. */
const TaskCard = ({ task, isNew, expanded, onToggle, onStatusChange, formatDate }) => {
  const { t } = useTranslation();
  const overdue = isOverdue(task);
  const priority = PRIORITY_STYLES[task.priority];
  const status = STATUS_STYLES[task.status];
  const StatusIcon = status?.icon;

  return (
    <li
      className={`tw-overflow-hidden tw-rounded-2xl tw-border tw-bg-white tw-shadow-sm tw-shadow-slate-900/[0.03] dark:tw-bg-white/[0.03] ${
        overdue ? 'tw-border-rose-200 dark:tw-border-rose-400/25' : 'tw-border-slate-200/80 dark:tw-border-white/10'
      } ${isNew ? 'tw-ring-2 tw-ring-teal-400/50' : ''}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="tw-flex tw-w-full tw-items-stretch tw-gap-3 tw-bg-transparent tw-p-3.5 tw-text-left tw-transition-colors hover:tw-bg-slate-50 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 sm:tw-p-4 dark:hover:tw-bg-white/[0.03]"
      >
        <span aria-hidden="true" className={`tw-w-1 tw-shrink-0 tw-rounded-full ${priority ? priority.bar : 'tw-bg-slate-400'}`} />
        <span className="tw-min-w-0 tw-flex-1">
          <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span className="tw-break-words tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{task.title}</span>
            {isNew && <Badge tone="accent">{t('tasks.newBadge')}</Badge>}
            {overdue && <Badge tone="danger">{t('tasks.overdue')}</Badge>}
          </span>
          <span className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1.5 tw-text-xs">
            {status && (
              <Badge tone={status.tone}>
                <StatusIcon aria-hidden="true" className="tw-h-3 tw-w-3" />
                {t(`tasks.status.${task.status}`)}
              </Badge>
            )}
            {priority && <span className={`tw-font-semibold ${priority.text}`}>{t(`tasks.priority.${task.priority}`)}</span>}
            {task.dueDate && (
              <span className={`tw-inline-flex tw-items-center tw-gap-1 ${overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-slate-500 dark:tw-text-slate-400'}`}>
                <LuCalendar aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {formatDate(task.dueDate)}
              </span>
            )}
          </span>
        </span>
        <LuChevronDown
          aria-hidden="true"
          className={`tw-h-5 tw-w-5 tw-shrink-0 tw-self-center tw-text-slate-400 tw-transition-transform tw-duration-200 ${expanded ? 'tw-rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="tw-overflow-hidden"
          >
            <div className="tw-space-y-4 tw-border-t tw-border-slate-200/80 tw-px-4 tw-pb-4 tw-pt-3.5 sm:tw-pl-8 dark:tw-border-white/10">
              {task.description && (
                <p className="tw-whitespace-pre-line tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{task.description}</p>
              )}
              {task.assignees && task.assignees.length > 0 && (
                <p className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">
                  <LuUsersRound aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                  <span className="tw-min-w-0">
                    <span className="tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('tasks.assignees')}: </span>
                    {task.assignees.map(a => a.name).join(', ')}
                  </span>
                </p>
              )}
              <div>
                <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{t('tasks.changeStatus')}</p>
                <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
                  {Object.entries(STATUS_STYLES).map(([key, style]) => {
                    const Icon = style.icon;
                    const selected = task.status === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onStatusChange(task.id, key)}
                        className={`tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 ${selected ? style.on : `tw-bg-transparent ${style.off}`}`}
                      >
                        <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                        {t(`tasks.status.${key}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

TaskCard.propTypes = {
  task: PropTypes.object.isRequired,
  isNew: PropTypes.bool,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
};

export default TaskCard;
